import { Component, DestroyRef, computed, inject, signal } from '@angular/core'
import { FormArray, FormBuilder, FormGroup } from '@angular/forms'
import { MatTableDataSource, MatTableModule } from '@angular/material/table'
import { FeatureAPIService, TaskAPIService } from '@modules'
import { BvAlertService } from '@ziptility/bv-alert'
import { NavigationService } from '@ziptility/services'
import { TaskViewStore } from '../task-view.store'
import { CommonModule } from '@angular/common'
import { IconsModule } from '@ziptility/icons'
import { MatMenuModule } from '@angular/material/menu'

import { ColumnPickerData, ViewColumnPickerComponent } from '../../views/column-picker.component'
import { Subscription, firstValueFrom } from 'rxjs'
import { tap } from 'rxjs/operators'
import { ActivatedRoute, Router } from '@angular/router'
import { ColumnRef, TableRow } from '../../views/models'
import { compact, flow, isEmpty, keys, min, orderBy, reduce, uniq, without } from 'lodash/fp'
import {
  ColumnInput,
  ExportTaskViewGQL,
  GetFeatureIdsForTasksGQL,
  GetTaskRowsGQL,
  GetTaskRowsPrevPageGQL,
  PageInfo,
  TaskRowFilterInput,
  TaskRowSortInput,
  TaskRowsConnection,
  ViewPropertyCategory,
} from '@ziptility/graphql'
import { SelectionModel } from '@angular/cdk/collections'
import { DialogService } from '@ziptility/components/dialogs'
import { Store } from '@ngrx/store'
import { MapActions, TaskActions } from '@ziptility/store/actions'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatDialog, MatDialogModule } from '@angular/material/dialog'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { TaskListTableService } from './task-list-table.service'
import { UserAvatarComponent } from '../../components/user-avatar/user-avatar.component'

@Component({
  selector: 'task-list-table',
  template: `
    <div class="flex flex-col h-full overflow-auto relative">
      <div class="py-2 flex items-center justify-between border border-gray-200 px-8">
        <div class="flex-grow flex space-x-3 items-center ">
          <input
            class="
            sm:text-sm
            border border-gray-500
            px-4
            py-2
            w-[300px]
            rounded-full
          "
            (keyup)="applyFilter($event)"
            placeholder="Filter results"
          />
          @if (dataSource.data.length && searching()) {
            <mat-spinner diameter="20"></mat-spinner>
          }
        </div>
        <div class="flex items-center space-x-2 ">
          <button
            class="bv-button text-gray-600 font-bold text-sm"
            [disabled]="isGeneratingExport"
            (click)="exportView()"
          >
            <i-feather name="download" class="mr-2"></i-feather> Export View
          </button>
          <button
            class="bv-button text-gray-600 font-bold text-sm"
            [disabled]="!canEditColumns()"
            (click)="editColumns()"
          >
            <i-feather name="columns" class="mr-2"></i-feather> Edit Columns
          </button>
        </div>
      </div>
      <div class="table-container relative flex-grow border border-gray-200">
        <table mat-table multiTemplateDataRows [dataSource]="dataSource" class="w-full">
          <ng-container matColumnDef="select" sticky>
            <th mat-header-cell *matHeaderCellDef width="40">
              <input
                type="checkbox"
                (change)="$event ? masterToggle() : null"
                [checked]="selection.hasValue() && isAllSelected()"
                [indeterminate]="selection.hasValue() && !isAllSelected()"
              />
            </th>
            <td mat-cell *matCellDef="let row" width="40">
              <input
                type="checkbox"
                (click)="$event.stopPropagation()"
                (change)="$event ? selection.toggle(row) : null"
                [checked]="selection.isSelected(row)"
              />
            </td>
          </ng-container>

          @for (column of taskColumnKeys(); track column) {
            <ng-container [matColumnDef]="column">
              <th mat-header-cell *matHeaderCellDef>{{ removeAssetPrefix(column) }}</th>
              <td mat-cell *matCellDef="let row">
                @switch (column) {
                  @case ('Status') {
                    <div
                      class="rounded-full py-1 px-2 text-white inline-block"
                      [ngClass]="{
                        'bg-green-500': row[column]?.value === 'Resolved',
                        'bg-gray-500': row[column]?.value === 'Open'
                      }"
                    >
                      @switch (row[column]?.value) {
                        @case ('Open') {
                          Open
                        }
                        @case ('Resolved') {
                          Completed
                        }
                      }
                    </div>
                  }
                  @case ('Assignee') {
                    <div class="inline-block">
                      @if (row[column]?.value) {
                        <user-avatar [fullName]="row[column]?.value" displayStyle="full"></user-avatar>
                      }
                    </div>
                  }
                  @case ('DateCreated') {
                    {{ row[column]?.value | date: 'short' }}
                  }
                  @case ('DateCompleted') {
                    {{ row[column]?.value | date: 'short' }}
                  }
                  @default {
                    @if (row[column]; as col) {
                      @switch (col.type) {
                        @case ('DateTimeType') {
                          {{ col.value | date: 'shortDate' }}
                        }
                        @case ('DecimalType') {
                          @if (col.format && col.format === 'currency') {
                            {{ col.value | currency }}
                          } @else {
                            {{ col.value }}
                          }
                        }
                        @case ('IntType') {
                          {{ col.value }}
                        }
                        @case ('BooleanType') {
                          {{ col.value }}
                        }
                        @case ('StringType') {
                          @if (col.value; as cellValue) {
                            @if (column === 'Title') {
                              <div
                                class="underline cursor-pointer"
                                (click)="$event.stopPropagation(); onSelectTask(row)"
                              >
                                {{ cellValue }}
                              </div>
                            } @else {
                              {{ cellValue }}
                            }
                          } @else {
                            ---
                          }
                        }
                      }
                    }
                  }
                }
              </td>
            </ng-container>
          }

          <ng-container matColumnDef="actions" stickyEnd>
            <th mat-header-cell *matHeaderCellDef width="60"></th>
            <td mat-cell *matCellDef="let row" width="60">
              <button mat-icon-button [matMenuTriggerFor]="menu" (click)="$event.stopPropagation()">
                <i-feather name="more-horizontal"></i-feather>
              </button>
              <mat-menu #menu="matMenu">
                <button mat-menu-item (click)="onViewTask(row)">View</button>
              </mat-menu>
            </td>
          </ng-container>

          @if (dataSource) {
            <!-- Row shown when there is no matching data. -->
            <tr class="mat-row" *matNoDataRow>
              <td class="mat-cell text-center" colspan="999">
                <div class="p-5">
                  @if (searching()) {
                    <div class="flex items-center justify-center w-full">
                      <mat-spinner diameter="20"></mat-spinner>
                    </div>
                  } @else {
                    No results
                  }
                </div>
              </td>
            </tr>
          }

          <tr mat-header-row *matHeaderRowDef="visibleColumns(); sticky: true"></tr>
          <tr
            [attr.taskId]="row.taskId"
            mat-row
            *matRowDef="let row; columns: visibleColumns()"
            (click)="selection.toggle(row)"
            class="task-detail-row hover:bg-gray-100"
          ></tr>
        </table>
      </div>
      @if (selection.hasValue()) {
        <div
          class="banner selection-banner absolute bottom-20 left-1/2 transform -translate-x-1/2 width-[300px] bg-blue-brand text-white rounded flex flex-row justify-between p-3 border-gray-200 border-t"
        >
          <button class="icon cursor-pointer" matTooltip="Reopen" (click)="onReopenSelections()">
            <i-feather name="rotate-ccw" class="feather-icon-sm"></i-feather>
            <div>Reopen</div>
          </button>
          <button
            [disabled]="!canCreateTasksForSelections"
            class="icon"
            [class.opacity-50]="!canCreateTasksForSelections"
            matTooltip="Create tasks on selections"
            (click)="onCreateTasksForSelections()"
          >
            <i-feather name="plus" class="feather-icon-sm"></i-feather>
            <div>Create Tasks</div>
          </button>
          <!-- <div class="icon cursor-pointer" matTooltip="Download" (click)="onExportSelections()">
          <i-feather name="table" class="feather-icon-sm"></i-feather>
          <div>Download</div>
        </div> -->
          <button class="icon cursor-pointer" matTooltip="Delete" (click)="onDeleteSelections()">
            <i-feather name="trash" class="feather-icon-sm"></i-feather>
            <div>Delete</div>
          </button>
        </div>
      }
      <div class="flex items-center mt-2 space-x-3 px-8 py-2">
        <button class="bv-button" [disabled]="!hasPreviousPage" (click)="previousPage()">
          <i-feather name="chevron-left"></i-feather>
        </button>
        <button class="bv-button" [disabled]="!hasNextPage" (click)="nextPage()">
          <i-feather name="chevron-right"></i-feather>
        </button>

        @if (pageInfo) {
          <div class="text-sm text-gray-600">{{ pagingDescription }}</div>
        }
      </div>
    </div>
  `,
  imports: [
    CommonModule,
    IconsModule,
    MatMenuModule,
    UserAvatarComponent,
    MatTableModule,
    MatProgressSpinnerModule,
    MatDialogModule,
  ],
  providers: [TaskListTableService],
  styles: [
    `
      .icon {
        padding: theme('spacing.2') theme('spacing.3');
        border-radius: theme('borderRadius.md');
        display: flex;
        align-items: center;
        justify-content: center;
        gap: theme('spacing.2');
        font-size: theme('fontSize.sm');
        font-weight: theme('fontWeight.semibold');
      }
    `,
  ],
})
export class TaskListTableComponent {
  readonly store = inject(Store)
  readonly destroyRef = inject(DestroyRef)
  readonly taskViewStore = inject(TaskViewStore)
  readonly taskService = inject(TaskAPIService)
  readonly featureService = inject(FeatureAPIService)
  readonly navigationService = inject(NavigationService)
  readonly router = inject(Router)
  readonly route = inject(ActivatedRoute)
  readonly fb = inject(FormBuilder)
  readonly getTaskRows = inject(GetTaskRowsGQL)
  readonly getTaskRowsPrevPage = inject(GetTaskRowsPrevPageGQL)
  readonly createTaskViewExcelExport = inject(ExportTaskViewGQL)
  readonly alertService = inject(BvAlertService)
  readonly dialogService = inject(DialogService)
  readonly getFeatureIds = inject(GetFeatureIdsForTasksGQL)
  readonly matDialog = inject(MatDialog)
  readonly gqlService = inject(TaskListTableService)

  protected readonly selectedColumns = signal<FormGroup[]>([])
  readonly selectedColumns$ = toObservable(this.selectedColumns)
  selectedColumnsSub: Subscription | undefined
  protected readonly searching = signal(false)
  protected readonly taskColumnKeys = computed<string[]>(() => {
    return this.selectedColumns().map((g) => g.value.key)
  })
  protected readonly visibleColumns = computed<string[]>(() => {
    return ['select', ...this.taskColumnKeys().filter((k) => !this.hiddenColumns.includes(k)), 'actions']
  })

  hiddenColumns = ['Status', 'Team', 'Asset:Team', 'Has Time Tracking', 'Has Inventory']
  dataSource: MatTableDataSource<TableRow>
  selection: SelectionModel<TableRow>
  columns: FormArray = this.fb.array([])
  totalCount: number | undefined
  pageSize = 50
  pageInfo: PageInfo | undefined
  columnSelectionsSub: Subscription | undefined
  isGeneratingExport = false

  get hasNextPage() {
    return this.pageInfo && this.pageInfo.hasNextPage
  }

  get hasPreviousPage() {
    return this.pageInfo && this.pageInfo.hasPreviousPage
  }

  get pagingDescription() {
    if (!this.totalCount) {
      return ''
    }
    return `${min([this.totalCount, this.pageSize])} / ${this.totalCount.toLocaleString()}`
  }

  get canCreateTasksForSelections(): boolean {
    return uniq(this.selection.selected.map((s) => s.taskTemplateId)).length === 1
  }

  canEditColumns = computed(() => {
    return this.selectedColumns().length
  })

  constructor() {
    this.selection = new SelectionModel<TableRow>(true, [])
    const dataSource = new MatTableDataSource<TableRow>([])
    const originalPredicate = dataSource.filterPredicate
    dataSource.filterPredicate = (data: TableRow, filter: string) => {
      const obj = flow(
        keys,
        reduce(
          (obj, key) => {
            obj[key] = data[key]?.value
            return obj
          },
          {} as Record<string, string>,
        ),
      )(data)
      return originalPredicate(obj as never, filter)
    }

    this.dataSource = dataSource
  }

  isAllSelected() {
    const numSelected = this.selection.selected.length
    const numRows = this.dataSource.data.length
    return numSelected == numRows
  }

  masterToggle() {
    this.isAllSelected() ? this.selection.clear() : this.dataSource.data.forEach((row) => this.selection.select(row))
  }

  ngOnInit() {
    this.taskViewStore.where$
      .pipe(
        tap((where: TaskRowFilterInput) => this.onFiltersChange(where)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe()
  }

  async onFiltersChange(where: TaskRowFilterInput) {
    if (isEmpty(where)) {
      return
    }
    const { order, select } = this.taskViewStore.state()
    this.fetch(where, order, select)
  }

  async refetch() {
    const where = await firstValueFrom(this.taskViewStore.where$)
    const { order, select } = this.taskViewStore.state()
    const res = await this.gqlService.fetch(where, order)
    this.onPage(res, select)
  }

  async fetch(where: TaskRowFilterInput, order: TaskRowSortInput[], select: string[]) {
    try {
      this.searching.set(true)
      const res = await this.gqlService.fetch(where, order)
      this.onPage(res, select)
    } catch (error) {
      console.log(error)
    } finally {
      this.searching.set(false)
    }
  }

  async nextPage() {
    if (!this.pageInfo?.endCursor) {
      return
    }
    const where = await firstValueFrom(this.taskViewStore.where$)
    const { order, select } = this.taskViewStore.state()
    const res = await this.gqlService.nextPage(this.pageInfo.endCursor, where, order)
    this.onPage(res, select)
  }

  async previousPage() {
    if (!this.pageInfo?.startCursor) {
      return
    }
    const where = await firstValueFrom(this.taskViewStore.where$)
    const { order, select } = this.taskViewStore.state()
    const res = await this.gqlService.previousPage(this.pageInfo.startCursor, where, order)
    this.onPage(res, select)
  }

  async editColumns() {
    this.matDialog.open(ViewColumnPickerComponent, {
      data: {
        columnOptions: this.columns.controls,
        selectedColumns: this.selectedColumns,
        hiddenColumnKeys: this.hiddenColumns,
        categories: [
          ViewPropertyCategory.TaskDetails,
          ViewPropertyCategory.TaskForm,
          ViewPropertyCategory.AssetDetails,
          ViewPropertyCategory.TimeTracking,
          ViewPropertyCategory.Inventory,
          ViewPropertyCategory.Other,
        ],
      } as ColumnPickerData,
    })
  }

  async onSelectTask(row: TableRow) {
    const t = await firstValueFrom(this.taskService.get(row.taskId))
    if (t.featureId && t.id) {
      this.router.navigate(['../', { outlets: { task: [t.id] } }], {
        relativeTo: this.route,
        queryParamsHandling: 'preserve',
      })
    }
  }

  onViewTask(row: TableRow) {
    this.router.navigate(['../', { outlets: { task: [row.taskId] } }], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    })
  }

  async applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value
    this.dataSource.filter = filterValue.trim().toLowerCase()
  }

  exportView() {
    this.downloadViewExport()
  }

  removeAssetPrefix(name: string) {
    return `${name.replace('Asset:', '')}`
  }

  onPage(response: TaskRowsConnection, taskColumnOrder: string[]) {
    const { pageInfo, totalCount, nodes } = response
    this.pageInfo = pageInfo
    this.totalCount = totalCount
    const { columns, rows } = this.gqlService.makeTableRows(nodes ?? [], taskColumnOrder)
    this.dataSource.data = rows
    this.setColumnFormModel(columns, taskColumnOrder)
  }

  private async setColumnFormModel(columns: ColumnRef[], select: string[] = []) {
    const columnMap = new Map()
    columns.forEach((c) => columnMap.set(c.key, c))

    const filters = await firstValueFrom(this.taskViewStore.selectedFilters$)
    const selectedFilterNames = filters.map((f) => f.title)
    const order = select.length ? select : this.gqlService.primaryColumnOrder
    const primaryColumns = columns.filter((c) => c.source === 'task' && order.includes(c.name))
    const nonPrimaryColumns = orderBy('name', 'asc', without(primaryColumns, columns))
    const ordered: ColumnRef[] = order.map((c) => columnMap.get(c)).concat(nonPrimaryColumns)
    const refs = uniq(compact(ordered))

    this.columns.clear()
    refs.forEach((c) => {
      const disabled = selectedFilterNames.includes(c.name)
      const visible = disabled ? true : order.includes(c.key)
      const group = this.fb.group<ColumnRef & { disabled: boolean }>({
        key: c.key,
        name: c.name,
        label: c.label,
        visible,
        source: c.source,
        disabled,
        category: c.category,
      })

      if (disabled) {
        group.disable()
      }

      this.columns.push(group)
    })

    this.columnSelectionsSub?.unsubscribe()

    this.setVisibleColumns()
    this.columns.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.setVisibleColumns()
    })

    this.selectedColumnsSub?.unsubscribe()
    this.selectedColumnsSub = this.selectedColumns$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((arr) => {
      const order = arr.filter((c: FormGroup) => c.value.visible).map((c: FormGroup) => c.value.key)
      this.taskViewStore.updateColumnOrder(order)
    })
  }

  setVisibleColumns() {
    const selectedColumns = this.columns.controls.filter((c) => c.value.visible) as FormGroup[]
    this.selectedColumns.set([...selectedColumns])
  }

  getColumnSelections(): { task: string[]; asset: string[] } {
    const columns: ColumnRef[] = this.selectedColumns().map((c) => c.value)

    return {
      task: columns.filter((c: ColumnRef) => c.source == 'task').map((c: ColumnRef) => c.name),
      asset: columns.filter((c: ColumnRef) => c.source == 'asset').map((c: ColumnRef) => c.name),
    }
  }

  async downloadViewExport() {
    const where = await firstValueFrom(this.taskViewStore.where$)
    const taskColumns: ColumnInput[] = []
    const assetColumns: ColumnInput[] = []

    this.selectedColumns()
      .map((c) => c.value)
      .forEach((c: ColumnRef, i) => {
        const input: ColumnInput = {
          name: c.name,
          order: i,
          excludeFromResults: this.hiddenColumns.includes(c.key),
        }
        if (c.source === 'task') {
          taskColumns.push(input)
        } else {
          assetColumns.push(input)
        }
      })

    try {
      this.isGeneratingExport = true
      const { data, error, errors } = await firstValueFrom(
        this.createTaskViewExcelExport.fetch(
          {
            where,
            taskColumns,
            assetColumns,
          },
          {
            fetchPolicy: 'network-only',
          },
        ),
      )

      console.log(data, error, errors)

      if (error) {
        this.alertService.error(error.message)
        return
      }
      if (errors) {
        errors.map((e) => this.alertService.error(e.message))
        return
      }

      if (data && data.exportTaskView && data.exportTaskView.excelFileUrl) {
        this.alertService.success('Downloading')
        this.downloadFile(data.exportTaskView.excelFileUrl)
      }
    } catch (error: any) {
      this.alertService.error(error.message)
    } finally {
      this.isGeneratingExport = false
    }
  }

  downloadFile = (url: string) => {
    const a = document.createElement('a')
    document.body.appendChild(a)
    a.setAttribute('style', 'display: none')
    a.href = url
    a.download = url
    a.click()
    a.remove()
  }

  async onReopenSelections() {
    const res = await firstValueFrom(
      this.dialogService.confirmDialogObservable(
        `Reopen Selections`,
        `Are you sure you want to reopen all selected tasks? This action cannot be undone.`,
        `Reopen`,
      ),
    )

    if (!res) {
      return
    }

    const arr = this.selection.selected.map((r) => this.taskService.reopen(r.taskId))
    await Promise.all(arr)
    this.alertService.success('Reopened')
    this.refetch()
    this.selection.clear()
  }

  onExportSelections() {
    const ids = this.selection.selected.map((r) => r.taskId)
    console.log(ids)
  }

  async onDeleteSelections() {
    const res = await firstValueFrom(
      this.dialogService.confirmDialogObservable(
        `Delete Selections`,
        `Are you sure you want to delete all selected tasks? This action cannot be undone.`,
        `Delete`,
      ),
    )

    if (!res) {
      return
    }

    await firstValueFrom(this.taskService.bulkDeleteTasks(this.selection.selected.map((r) => r.taskId)))
    this.alertService.success('Deleted')
    this.refetch()
    this.selection.clear()
  }

  async onCreateTasksForSelections() {
    const ids = this.selection.selected.map((s) => s.taskId)
    if (isEmpty(ids)) {
      return
    }
    const {
      data: { taskRows },
    } = await firstValueFrom(
      this.getFeatureIds.fetch({
        where: {
          taskId: {
            in: ids,
          },
        },
        first: 1000,
      }),
    )

    const featureIds = taskRows?.nodes?.map((r) => r.assetRow?.assetId) ?? []
    if (isEmpty(featureIds)) {
      return
    }
    const features = await firstValueFrom(this.featureService.list(uniq(featureIds)))
    if (isEmpty(features)) {
      return
    }
    const featureSelections = features.map((f) => ({ id: f.id!, layerId: f.layerId }))
    this.store.dispatch(MapActions.selectFeatures({ features: featureSelections }))
    this.store.dispatch(TaskActions.beginBulkTaskCreate({ features: featureSelections }))
    this.router.navigate(['../', { outlets: { task: 'create' } }], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    })
  }
}
